import {EditorLayer, EncoderProject} from '../../types';
import {encodeMedia} from './rendering';
import {CBProgressCallback} from './ffmpeg';
import {defaultVideoDuration} from './util/genVideoFromImage';

export const getActiveProjectLayer = (
  layers: EditorLayer[],
  currentPosition: number,
  type: 'audio' | 'video' = 'video',
) => {
  let activeMedia: EncoderProject | undefined;
  let layerCount = 0;
  let offset = 0;
  let layer: EditorLayer | undefined;

  while (activeMedia?.id === undefined) {
    layer = layers[layerCount];

    if (layerCount > layers.length + 1) {
      console.error('layers', layers, layerCount);
      throw 'no_layer_found';
    }

    if (layer?.id === undefined) {
      layerCount++;
      continue;
    }

    if (type === 'video' && layer?.editor?.videoDisabled === true) {
      layerCount++;
      continue;
    }

    if (type === 'audio' && layer?.editor?.muted === true) {
      layerCount++;
      continue;
    }

    const layerMedia = layer.media;
    activeMedia = layerMedia!.reduce((a, media: EncoderProject) => {
      if (a.id) return a;

      a +=
        (media?.encoding?.end ||
          media?.duration ||
          defaultVideoDuration) - (media?.encoding?.start || 0);
      if (a >= currentPosition) return media;

      offset = a;
      return a;
    }, 0);
    layerCount++;
  }
  return {offset, layer, activeLayer: layerCount, activeMedia};
};

export const getNextActiveProjectMedia = (
  layers: EditorLayer[],
  startPosition: number,
  media: EncoderProject,
) => {
  const currentDuration =
    (media?.encoding?.end || media?.duration) -
    (media?.encoding?.start || 0);
  const nextPosition = startPosition + currentDuration + 1;
  const next = getActiveProjectLayer(layers, nextPosition);
  // console.info('nextPosition', nextPosition, next);
  return next;
};

export const getTotalTime = (layers: EditorLayer[]): number => {
  const time = layers.reduce((a, layer) => {
    const t = layer.media?.reduce((agv, media) => {
      agv +=
        (media?.encoding?.end || media?.duration) -
        (media?.encoding?.start || 0);
      return agv;
    }, 0);
    if (!t) return a;
    if (a > t) return a;
    if (t > a) return t;
  }, 0);
  return time;
};

export const getProjectMedia = async (
  layers: EditorLayer[],
  currentPosition: number,
  cb: CBProgressCallback,
) => {
  const {offset, layer, activeLayer, activeMedia} =
    getActiveProjectLayer(layers, currentPosition);

  const offsetPosition =
    currentPosition -
    offset +
    parseFloat((activeMedia?.encoding.start || '0') as string);
  const totalTime = getTotalTime(layers);
  console.info(
    'getProjectMedia offset',
    offsetPosition,
    currentPosition,
    offset,
    activeMedia?.encoding?.start,
    activeMedia?.id,
  );
  const encode = async (
    mediaToEncode: EncoderProject,
    offsetP: number,
  ): Promise<string> => {
    const e = encodeMedia(mediaToEncode, cb, true);
    return new Promise(res => {
      e.then(async r => {
        res(r as string);
        try {
          const next = getNextActiveProjectMedia(
            layers,
            offsetP,
            mediaToEncode,
          );
          if (next) {
            await encodeMedia(next?.activeMedia, () => {}, true);
          }
        } catch (err) {
          console.error('prepare next video error', err);
        }
      });
    });
  };
  const url =
    activeMedia?.id !== undefined
      ? await encode(activeMedia, offset)
      : null;
  let audioUrl: string | undefined;
  let audioLayer:
    | ReturnType<typeof getActiveProjectLayer>
    | undefined;
  let audioPosition: number | undefined;
  try {
    audioLayer = getActiveProjectLayer(
      layers,
      currentPosition,
      'audio',
    );
    audioPosition =
      currentPosition -
      audioLayer?.offset +
      parseFloat(
        (audioLayer?.activeMedia?.encoding?.start || '0') as string,
      );
    audioUrl =
      audioLayer.activeMedia?.id !== undefined
        ? await encode(audioLayer.activeMedia, audioLayer.offset)
        : undefined;
  } catch (err) {
    console.error('audio layer error', err);
  }
  return {
    url,
    currentPosition: offsetPosition,
    muted: layer?.editor?.muted,
    activeMedia,
    totalTime,
    offset,
    activeLayer,
    audioLayer,
    audioUrl,
    audioPosition,
  };
};
